/**
 * `LoggedAsLine` — the read-only audit line every case-action modal shows in its
 * footer: who is acting, and when the modal was opened.
 *
 * Presentational and stateless: the CONTAINER snapshots `{name, at}` once per open and passes it
 * frozen, so the line can never drift with the clock while the modal is up.
 *
 * The label is a prop so a modal can override it; every case-action modal uses the default "Logged as".
 */
import { Text } from '@chakra-ui/react';
import React from 'react';

import { INK, INK_SOFT } from '../../styles/tokens';
import { formatLoggedAs, type LoggedAsStyle } from './formatLoggedAs';

export interface LoggedAsLineProps {
  readonly name: string;
  readonly at: Date;
  /** IANA zone — pins the rendered instant for deterministic stories/tests. */
  readonly timeZone?: string;
  readonly label?: string;
  /** Named `dateStyle`, not `style` — a React prop called `style` reads as the CSS one. */
  readonly dateStyle?: LoggedAsStyle;
  readonly testId?: string;
}

export const LoggedAsLine: React.FC<LoggedAsLineProps> = ({
  name,
  at,
  timeZone,
  label = 'Logged as',
  dateStyle = 'numeric',
  testId = 'logged-as'
}) => (
  <Text
    data-testid={testId}
    textStyle="Body 1"
    color={INK_SOFT}
    flex="0 1 auto"
    flexShrink={1}
  >
    {label}{' '}
    <Text as="b" color={INK}>
      {name}
    </Text>{' '}
    · {formatLoggedAs(at, timeZone, dateStyle)}
  </Text>
);
